import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity } from "react-native";
import { AppIcon, AppText } from "@toothfairy/shared-ui";

// Scheduling-related relationship chips for an agent, mirroring the
// RelationshipPanel pattern. Surfaces, on the agent settings screen, the
// scheduled jobs + event triggers that fire THIS agent — Saves the user from
// having to switch to the Jobs / Triggers sections to see what invokes it.
//
//   Scheduled at  → cronjobs bound to this agent (`agentID === agent.id`)
//   Triggered by  → triggers bound to this agent (`agentID === agent.id`)
//
// Each item: { id, name, type: "job" | "trigger", extra? }

const TYPE_CONFIG = {
  job: {
    icon: AppAssets?.icons?.clock,
    color: "#0891b2",
    labelKey: "dashboard.modelView.birdEyeJob",
  },
  trigger: {
    icon: AppAssets?.icons?.orbit,
    color: "#dc2626",
    labelKey: "dashboard.modelView.birdEyeTrigger",
  },
};

const SchedulingChip = ({ item, currentTheme, onPress }) => {
  const { t } = useTranslation();
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.job;

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={onPress ? 0.6 : 1}
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 6,
        paddingHorizontal: 10,
        marginHorizontal: 4,
        marginVertical: 3,
        backgroundColor: `${config.color}10`,
        borderRadius: 6,
        borderWidth: 0.5,
        borderColor: `${config.color}30`,
      }}
    >
      <AppIcon
        icon={config.icon}
        color={config.color}
        size={12}
        style={{ marginRight: 6 }}
      />
      <AppText
        style={{
          fontSize: 13,
          color: currentTheme?.theme?.font_color,
          fontWeight: "500",
          maxWidth: 220,
        }}
        numberOfLines={1}
      >
        {item.name}
      </AppText>
      {item?.extra ? (
        <AppText
          style={{
            fontSize: 10,
            color: config.color,
            marginLeft: 6,
            fontStyle: "italic",
          }}
        >
          {item.extra}
        </AppText>
      ) : (
        <AppText
          style={{
            fontSize: 10,
            color: config.color,
            marginLeft: 6,
            fontStyle: "italic",
          }}
        >
          {t(config.labelKey)}
        </AppText>
      )}
    </TouchableOpacity>
  );
};

const SchedulingSection = ({
  title,
  items,
  emptyMessage,
  icon,
  currentTheme,
  t,
  onItemClick,
}) => {
  if (!items || items.length === 0) {
    return (
      <AppExpander
        style={{ minWidth: 250, zIndex: -1, marginTop: 10 }}
        title={`${title} (0)`}
        icon={icon}
        isExpandedOnLoad={false}
        isEnabled={false}
      >
        <View style={{ padding: 10 }}>
          <AppText
            style={{
              fontSize: 13,
              color: currentTheme?.theme?.darkGrey,
              fontStyle: "italic",
            }}
          >
            {emptyMessage}
          </AppText>
        </View>
      </AppExpander>
    );
  }

  return (
    <AppExpander
      style={{ minWidth: 250, zIndex: -1, marginTop: 10 }}
      title={`${title} (${items.length})`}
      icon={icon}
      isExpandedOnLoad={false}
    >
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          paddingVertical: 4,
          paddingHorizontal: 6,
        }}
      >
        {items.map((item, idx) => (
          <SchedulingChip
            key={`${item.id}-${idx}`}
            item={item}
            currentTheme={currentTheme}
            onPress={onItemClick ? () => onItemClick(item) : undefined}
          />
        ))}
      </View>
    </AppExpander>
  );
};

const AgentSchedulingPanel = ({
  jobs = [],
  triggers = [],
  isEdit = false,
  onItemClick,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();

  const memoizedJobs = useMemo(
    () => jobs.filter((item) => item && item.id && item.name),
    [JSON.stringify(jobs)],
  );

  const memoizedTriggers = useMemo(
    () => triggers.filter((item) => item && item.id && item.name),
    [JSON.stringify(triggers)],
  );

  // Only meaningful on an existing agent — there is no schedule/trigger for an
  // agent that hasn't been saved yet.
  if (!isEdit) return null;

  const hasJobs = memoizedJobs.length > 0;
  const hasTriggers = memoizedTriggers.length > 0;

  if (!hasJobs && !hasTriggers) {
    return (
      <View style={{ zIndex: -1, marginTop: 10 }}>
        <AppExpander
          style={{ minWidth: 250, zIndex: -1 }}
          title={t("common.scheduling")}
          icon={AppAssets?.icons?.clock}
          isExpandedOnLoad={false}
          isEnabled={false}
        >
          <View style={{ padding: 10 }}>
            <AppText
              style={{
                fontSize: 13,
                color: currentTheme?.theme?.darkGrey,
                fontStyle: "italic",
              }}
            >
              {t("common.notScheduledAnywhere")}
            </AppText>
          </View>
        </AppExpander>
      </View>
    );
  }

  return (
    <View style={{ zIndex: -1 }}>
      {hasJobs && (
        <SchedulingSection
          title={t("common.scheduledAt")}
          items={memoizedJobs}
          emptyMessage={t("common.notScheduledAnywhere")}
          icon={AppAssets?.icons?.clock}
          currentTheme={currentTheme}
          t={t}
          onItemClick={onItemClick}
        />
      )}
      {hasTriggers && (
        <SchedulingSection
          title={t("common.triggeredBy")}
          items={memoizedTriggers}
          emptyMessage={t("common.notTriggeredByAnything")}
          icon={AppAssets?.icons?.orbit}
          currentTheme={currentTheme}
          t={t}
          onItemClick={onItemClick}
        />
      )}
      {!hasJobs && (
        <SchedulingSection
          title={t("common.scheduledAt")}
          items={[]}
          emptyMessage={t("common.notScheduledAnywhere")}
          icon={AppAssets?.icons?.clock}
          currentTheme={currentTheme}
          t={t}
        />
      )}
      {!hasTriggers && (
        <SchedulingSection
          title={t("common.triggeredBy")}
          items={[]}
          emptyMessage={t("common.notTriggeredByAnything")}
          icon={AppAssets?.icons?.orbit}
          currentTheme={currentTheme}
          t={t}
        />
      )}
    </View>
  );
};

export default AgentSchedulingPanel;